import React, { useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, ScrollRestoration, Outlet } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Home from './pages/Home';
import Account from './pages/Account';
import ServiceRequest from './pages/ServiceRequest';
import SpareParts from './pages/SpareParts';
import Orders from './pages/Orders';
import Cart from './pages/Cart';
import OrderAddress from './pages/OrderAddress';
import OrderDetail from './pages/OrderDetail';
import ServiceDetail from './pages/ServiceDetail';
import ServiceRequests from './pages/ServiceRequests';
import Warranties from './pages/Warranties';
import WarrantyDetail from './pages/WarrantyDetail';
import WarrantyStart from './pages/WarrantyStart';
import Announcements from './pages/Announcements';
import ServiceBulletins from './pages/ServiceBulletins';
import AccountSummary from './pages/AccountSummary';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Layout from './components/Layout';
import { UserProvider } from './contexts/UserContext';
import { MenuProvider } from './contexts/MenuContext';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { userData, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!userData || !userData.LoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollRestoration/>
        <Outlet />
      </>
    ),
    children: [
      {
        path: "/login",
        element: <Login />
      },
      {
        path: "/",
        element: <ProtectedRoute><Home /></ProtectedRoute>
      },
      {
        path: "/account",
        element: <ProtectedRoute><Account /></ProtectedRoute>
      },
      {
        path: "/service-request",
        element: <ProtectedRoute><ServiceRequest /></ProtectedRoute>
      },
      {
        path: "/service-request/:id",
        element: <ProtectedRoute><ServiceRequest /></ProtectedRoute>
      },
      {
        path: "/spare-parts",
        element: <ProtectedRoute><SpareParts /></ProtectedRoute>
      },
      {
        path: "/orders",
        element: <ProtectedRoute><Orders /></ProtectedRoute>
      },
      {
        path: "/cart",
        element: <ProtectedRoute><Cart /></ProtectedRoute>
      },
      {
        path: "/order-address",
        element: <ProtectedRoute><OrderAddress /></ProtectedRoute>
      },
      {
        path: "/order/:id",
        element: <ProtectedRoute><OrderDetail /></ProtectedRoute>
      },
      {
        path: "/service/:id",
        element: <ProtectedRoute><ServiceDetail /></ProtectedRoute>
      },
      {
        path: "/service-requests",
        element: <ProtectedRoute><ServiceRequests /></ProtectedRoute>
      },
      {
        path: "/warranties",
        element: <ProtectedRoute><Warranties /></ProtectedRoute>
      },
      {
        path: "/warranty/:id",
        element: <ProtectedRoute><WarrantyDetail /></ProtectedRoute>
      },
      {
        path: "/warranty-start",
        element: <ProtectedRoute><WarrantyStart /></ProtectedRoute>
      },
      {
        path: "/announcements",
        element: <ProtectedRoute><Announcements /></ProtectedRoute>
      },
      {
        path: "/service-bulletins",
        element: <ProtectedRoute><ServiceBulletins /></ProtectedRoute>
      },
      {
        path: "/account-summary",
        element: <ProtectedRoute><AccountSummary /></ProtectedRoute>
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicy />
      },
      {
        path: "*",
        element: <Navigate to="/" replace />
      }
    ]
  }
]);

function App() {
  // Service Worker Registration - Only in production
  useEffect(() => {
    // Only register service worker in production
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
           
            // Check for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // Force the new service worker to take control
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                  }
                });
              }
            });
          })
          .catch((registrationError) => {
            console.error('SW registration failed:', registrationError);
          });
      });
      
      // Listen for controlling service worker changes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      
      // Clean up old caches on first visit after PWA uninstall
      if (!localStorage.getItem('sw-init')) {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
        }
        localStorage.setItem('sw-init', 'true');
      }
    } else if (process.env.NODE_ENV === 'development') {
      // In development, unregister any existing service workers
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (let registration of registrations) {
            registration.unregister().then(() => {
            });
          }
        });
      }
    }
  }, []);

  return (
    <ThemeProvider>
      <UserProvider>
        <AuthProvider>
          <MenuProvider>
            <RouterProvider router={router} />
          </MenuProvider>
        </AuthProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;

